import dayjs from "dayjs";

import { MIN_FARE_LEAD_DAYS } from "./constants";
import type { TripDates } from "./types";

/**
 * Formats trip dates compactly: "Mar 3 – 5, 2027", "Mar 30 – Apr 2, 2027".
 * @param dates - Trip dates.
 * @returns The label.
 * @throws Never.
 */
export const formatTripDates = ({ start, end }: TripDates): string => {
  const from = dayjs(start);
  const to = dayjs(end);
  if (from.isSame(to, "day")) return from.format("MMM D, YYYY");
  if (from.isSame(to, "month")) return `${from.format("MMM D")} – ${to.format("D, YYYY")}`;
  if (from.isSame(to, "year")) return `${from.format("MMM D")} – ${to.format("MMM D, YYYY")}`;
  return `${from.format("MMM D, YYYY")} – ${to.format("MMM D, YYYY")}`;
};

/**
 * Trip dates for tight spaces, the year said once: "Nov 17–20, 2026", "Nov 30–Dec 2, 2026",
 * "Dec 30, 2026–Jan 2, 2027".
 * @param dates - Trip dates.
 * @param withYear - False drops the year ("Nov 17–20"), as generated titles always had it.
 * @returns The label.
 * @throws Never.
 */
export const formatShortTripDates = ({ start, end }: TripDates, withYear = true): string => {
  const from = dayjs(start);
  const to = dayjs(end);
  if (!withYear) {
    if (from.isSame(to, "day")) return from.format("MMM D");
    return from.isSame(to, "month")
      ? `${from.format("MMM D")}–${to.format("D")}`
      : `${from.format("MMM D")}–${to.format("MMM D")}`;
  }
  if (from.isSame(to, "day")) return from.format("MMM D, YYYY");
  if (from.isSame(to, "month")) return `${from.format("MMM D")}–${to.format("D, YYYY")}`;
  if (from.isSame(to, "year")) return `${from.format("MMM D")}–${to.format("MMM D, YYYY")}`;
  return `${from.format("MMM D, YYYY")}–${to.format("MMM D, YYYY")}`;
};

/**
 * Whether fares can be priced for these dates: the trip starts at least
 * {@link MIN_FARE_LEAD_DAYS} days from today. Past dates, or dates too close to book a group,
 * have no fares to find.
 * @param dates - Trip dates.
 * @param today - Today (for tests).
 * @returns True when a fare search makes sense.
 * @throws Never.
 */
export const canPriceFares = (dates: TripDates, today = dayjs()): boolean =>
  !dayjs(dates.start).isBefore(today.startOf("day").add(MIN_FARE_LEAD_DAYS, "day"));
